import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  LinkOutlined,
  MailOutlined,
  PoweroffOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { Button } from 'antd';
import { isAllEnabled, enableAll, disableAll } from '@/utils/feature-flags';
import { getUserType } from './utils/auth';
import TokenExpirationMonitor from './components/TokenExpirationMonitor';
import { Auth0Provider } from '@auth0/auth0-react';
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_PATH } from '@/utils/auth0Config';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/auth/login';
const resetPasswordPath = '/reset-password';
const requestPasswordResetPath = '/request-password-reset';
const auth0CallbackPath = AUTH0_CALLBACK_PATH;

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg;
    } catch (error) {
      if (history.location.pathname !== loginPath) {
        history.push(`${loginPath}?error=session_expired`);
      }
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    location.pathname !== loginPath &&
    location.pathname !== resetPasswordPath &&
    location.pathname !== requestPasswordResetPath &&
    location.pathname !== auth0CallbackPath &&
    location.pathname !== '/auth/login' &&
    location.pathname !== '/admin/login' &&
    location.pathname !== '/auth-test'
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [
      // <Question key="doc" /> /*<SelectLang key="SelectLang" />*/,
      getUserType() === 'admin' && (
        <Button
          key="admin-help-ticket"
          onClick={() => {
            window.open('https://aurora-innolabs.tawk.help/', '_blank');
          }}
          size="small"
          icon={<QuestionCircleOutlined />}
        >
          Help (thru Ticket)
        </Button>
      ),
      getUserType() === 'admin' && (
        <Button
          key="admin-help-email"
          size="small"
          href="mailto:tickets@aurora-web-it.p.tawk.email"
          icon={<MailOutlined />}
        >
          Help (thru Email)
        </Button>
      ),
      <Button
        key="toggle-beta-features"
        onClick={() => {
          if (isAllEnabled()) {
            disableAll();
          } else {
            enableAll();
          }
          window.location.reload();
        }}
        size="small"
        icon={<PoweroffOutlined />}
      >
        {isAllEnabled() ? 'Disable' : 'Enable'} Beta Features
      </Button>,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      // content: initialState?.currentUser?.name,
    },
    footerRender: () => (
      <>
        <Footer />
      </>
    ),
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        location.pathname !== auth0CallbackPath &&
        location.pathname !== '/auth/login' &&
        location.pathname !== '/admin/login' &&
        location.pathname !== '/auth-test'
      ) {
        console.log('no login', initialState);
        history.push(`${loginPath}?error=session_expired`);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <TokenExpirationMonitor />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

export function rootContainer(container: React.ReactNode) {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}${AUTH0_CALLBACK_PATH}`,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      {container}
    </Auth0Provider>
  );
}
